import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Loading } from '@/components/loaders';
import { resolveConnectorFlowErrorMessage } from '@/features/connectors/flows';
import { createWhatsappEditFlowHandler, useWhatsappChannelFlow, whatsappEditSelectionKey, type WhatsappEditSelection } from '@/features/connectors/whatsapp';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import RouteConstants from '@/features/router/RouteConstants';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { ToastType } from '@/models/toast';
import { useCompanyValues } from '@/providers';
import { useAppDispatch } from '@/reducers';
import { audiencerateApi } from '@/services/audiencerate';
import {
  PrepareConnectorFlowStepResponse,
  useCreateFlowInstanceMutation,
  useGetConnectorInstancesQuery,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import type { WhatsappTemplateEditLocationState } from '../WhatsappTemplateList/useWhatsappTemplateListActions';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

type CreateTemplateOpts = {
  flowParams?: FlowFormData;
  flowMappings?: FlowFormData;
  /** When true, skip mappings-form validation (flow finished without a mappings UI). */
  fromFlowComplete?: boolean;
};

const readEditSelection = (state: unknown): WhatsappEditSelection | undefined => {
  const editSelection = (state as WhatsappTemplateEditLocationState | null | undefined)?.editSelection;
  if (!editSelection?.name || !editSelection?.language) return undefined;
  return editSelection;
};

const EditInit = ({ workspaceId, whatsappConnectorFlowId, connectorInstanceId, whatsappConnectorFlow }) => {
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const { t } = useTranslation();
  const appDispatch = useAppDispatch();
  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const history = useHistory();
  const location = useLocation();
  const editSelection = readEditSelection(location.state);
  const resolveStepAutoFill = useMemo(() => createWhatsappEditFlowHandler(editSelection), [editSelection]);
  const selectionKey = whatsappEditSelectionKey(editSelection);

  const [prepareStep] = usePrepareConnectorFlowStepMutation();

  const [formData, setFormData] = useState<FlowFormData>({});
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const { data: instances } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });
  const [createFlowInstance] = useCreateFlowInstanceMutation();

  useEffect(() => {
    const fetchPrepareData = async () => {
      const response = await prepareStep({
        workspaceId,
        flowId: whatsappConnectorFlowId,
        audienceId: audienceId!,
        stepId: 'step1',
        stepData: {},
        connectorInstanceId,
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);
      const prepareResultResponse = translatedResponse?.configuration?.elements?.params;

      setPrepareResult(prepareResultResponse as PrepareStepResult);
    };
    if (!!connectorInstanceId && !!whatsappConnectorFlowId && !!audienceId) {
      fetchPrepareData();
    }
  }, [audienceId, connectorInstanceId, whatsappConnectorFlowId, prepareStep, currentLanguage, workspaceId]);

  const handleEditTemplate = async (opts?: CreateTemplateOpts) => {
    if (isSubmitting) {
      return { success: false, message: 'Already submitting' };
    }

    const instance = instances?.find((inst) => inst.connectorId === whatsappConnectorFlow?.connectorId);

    if (!instance) {
      const message = String(t('campaign:noConnectorInstanceFound'));
      setCreateError(message);
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: message,
        })
      );
      return { success: false, message };
    }

    if (!audienceId) {
      const message = String(t('campaign:missingAudience.toast'));
      setCreateError(message);
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: message,
        })
      );
      return { success: false, message };
    }

    const flowParams = opts?.flowParams ?? formData;
    const flowMappings = opts?.flowMappings ?? mappingData;

    const payload = buildFlowInstancePayload({
      workspaceId,
      connectorFlowId: whatsappConnectorFlowId!,
      connectorInstanceId: instance.id,
      audienceId,
      flowParams,
      flowMappings,
    });

    setCreateError(null);
    setIsSubmitting(true);
    try {
      await createFlowInstance({ ...payload, lang: currentLanguage }).unwrap();
      appDispatch(
        showToast({
          title: String(t('campaign:whatsappTemplates.templateEditedSuccessfully')),
          type: ToastType.SUCCESS,
        })
      );
      appDispatch(audiencerateApi.util.invalidateTags(['Campaigns']));
      history.push(RouteConstants.whatsappTemplates);
      return { success: true };
    } catch (error) {
      const errorMessage = resolveConnectorFlowErrorMessage(error, currentLanguage) ?? String(t('campaign:flowInstanceCreationFailed'));
      setCreateError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsSubmitting(false);
    }
  };

  const multiStepFlowFormProps = {
    id: 'template-edit-workaround-form',
    showTemplatePreview: true,
    audienceId: audienceId!,
    connectorInstanceId: connectorInstanceId!,
    connectorFlowId: whatsappConnectorFlowId!,
    paramsConfig: prepareResult || {},
    resolveStepAutoFill,
    onComplete: (allParams: FlowFormData) => {
      setFormData(allParams);
      setMappingData({});
      void handleEditTemplate({ flowParams: allParams, flowMappings: {}, fromFlowComplete: true });
    },
  };

  const isConnectorReady = !!connectorInstanceId && !!whatsappConnectorFlowId;
  const isPreparePending = hasAudience && isConnectorReady && !prepareResult;

  if (isAudienceLoading || isPreparePending) {
    return <Loading />;
  }

  if (isAudienceSettled && !hasAudience) {
    return <AudienceRequiredEmptyState />;
  }

  return (
    <div className='space-y-4'>
      <div>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('campaign:whatsappTemplates.editTemplatesHub')}</h2>
      </div>
      {createError ? <div className='rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>{createError}</div> : null}
      <ErrorBoundary>
        {prepareResult ? (
          <MultiStepFlowForm key={`${whatsappConnectorFlowId}-${audienceId}-${selectionKey}`} {...multiStepFlowFormProps} isInAction={isSubmitting} />
        ) : null}
      </ErrorBoundary>
    </div>
  );
};

export const WhatsappTemplateEditWorkaround = () => {
  const { id: workspaceId } = useWorkspace();

  const { flow: whatsappConnectorFlow, connectorInstanceId, isLoading } = useWhatsappChannelFlow('templateEdit');
  const whatsappConnectorFlowId = whatsappConnectorFlow?.id;

  if (isLoading) {
    return <Loading />;
  }

  return (
    <EditInit
      workspaceId={workspaceId}
      whatsappConnectorFlowId={whatsappConnectorFlowId}
      connectorInstanceId={connectorInstanceId}
      whatsappConnectorFlow={whatsappConnectorFlow}
    />
  );
};
